import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ADIPdpBannerSlide from "@NA/Components/ProductDetails/ADIPdpBannerSlide";
import bannerCarousel from "@NA/Handlers/ADIGetBannerCarouselHandler";
import BannerArrow from "@NA/Icons/BannerArrow";
import { BannerCarouselList } from "@NA/Services/GetBannerCarousel";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { FC, useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
    };
    extendedStyles?: ADIBannerStyles;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const billToId = state.context.session.billToId;
    let acNoSession;
    if (billToId) {
        acNoSession = state.data.billTos.byId[billToId]?.customerNumber
    }
    return {
        session: state.context.session,
        acNoSession,
        location: getLocation(state),
        isLoading: state.ADIGlobalData.bannerCarouselStore.isLoading,
        bannerCarouselList: state.ADIGlobalData.bannerCarouselStore.bannerCarousel?.bannerCarouselList,
        isProductLoading: state.pages.productDetails.isProductLoading,
        userRoles: state.context.session.userRoles,
        loyaltyUserStateStore: state.ADIGlobalData.loyaltyRewardUserStore,
        userNameSession: state.context.session.userName?.toLocaleLowerCase(),
    };
};

const mapDispatchToProps = {
    bannerCarousel,
};

export interface ADIBannerStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    pdpBannerLink?: LinkPresentationProps;
}

export const adiBannerStyles: ADIBannerStyles = {
    slideshowWrapper: {
        css: css`
            padding: 0;
            position: relative;
            display: flex;
            justify-content: center;
        `,
    },
    prevArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 2;
            top: 50%;
            left: 80px;
            margin-top: -20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
            @media all and (max-width:1450px) {
                left: 20px;
            }
        `,
    },
    prevArrowButton: {
        css: css`
            transform: rotate(180deg);
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    iconProps: {
        size: 40,
        color: "primary",
        css: css`
            > svg {
                color: ${getColor("primary")};
            }
            top: -1.875em;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: ${ADIColors.white};
            background-repeat: no-repeat;
            height: auto;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            max-width: 100%;
            margin: 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: -24px;
            position: absolute;
            width: 100%;
            bottom: 30px;
        `,
    },
    dotButton: {
        css: css`
            min-height: 9px;
            height: 13px;
            width: 13px;
            padding: 0;
            margin: 0 4px;
            border: 1px solid ${ADIColors.blue2};
            border-radius: 50%;
            background: ${getColor("common.border")};
            &:hover {
                background: ${ADIColors.white};
            }
            &:active {
                background: ${ADIColors.blue2};
            }
            &:focus {
                background: ${ADIColors.blue2};
                outline: none;
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -20px;
            right: 80px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
            @media all and (max-width:1450px) {
                right: 20px;
            }
        `,
    },
    nextArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasProduct;

export const ADIPdpBanner: FC<Props> = ({ fields, extendedStyles, acNoSession, bannerCarousel, isLoading, bannerCarouselList,
    product, isProductLoading, userRoles, loyaltyUserStateStore, userNameSession, session, }) => {
    const styles = useMergeStyles("slideshow", adiBannerStyles, extendedStyles);
    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const isProRewardsUser = userRoles && userRoles.includes("ADIProRewards");
    const invitedStatus = ["A", "B", "O"];
    const isLoyaltyLoading = loyaltyUserStateStore.isLoading;
    const isAuthenticated = session.isAuthenticated;
    const accNoSession = acNoSession?.toString().split('-')[0];
    const accNoLTY = loyaltyUserStateStore.user.userId !== undefined ? loyaltyUserStateStore.user.userId : ""
    const emailLTY = loyaltyUserStateStore.user.email !== undefined ? loyaltyUserStateStore.user.email?.toLocaleLowerCase() : ""
    const isRewardInvitedUser = (invitedStatus.indexOf(loyaltyUserStateStore.user.status) > -1) && !isProRewardsUser &&
        accNoLTY === accNoSession && loyaltyUserStateStore.user.isEnrolled === false && userNameSession === emailLTY;
    const isRewardEnrolledUser = (invitedStatus.indexOf(loyaltyUserStateStore.user.status) > -1) && accNoLTY === accNoSession
        && loyaltyUserStateStore.user.isEnrolled && (isProRewardsUser || false);

    // Load banner from page to avoid double api calls
    // useEffect(() => {
    //     if (!product.properties.productcategoryurl) return;

    //     const categoryUrl = product.properties["productcategoryurl"];
    //     const path = categoryUrl.split("/");
    //     const len = path.length;

    //     const pageUrl = `${categoryUrl}/${product.productNumber}`;
    //     const l2Cat = len >= 5 ? path[len - (len === 6 ? 2 : 1)] : "";
    //     const l3Cat = len === 6 ? path[len - 1] : "";

    //     if (!isAuthenticated || !isLoyaltyLoading) {
    //         const bannerParams = {
    //             isPreview: false,
    //             pageUrl,
    //             adiNumber: product.productNumber,
    //             brand: product.brand?.urlSegment || "",
    //             personaType: "All",
    //             isPageTypePdp: true,
    //             l2Category: l2Cat,
    //             l3Category: l3Cat,
    //             ...(isAuthenticated && { isRewardInvitedUser, isRewardEnrolledUser }),
    //         };
    //         bannerCarousel(bannerParams);
    //     }
    // }, [isLoyaltyLoading, product.properties.productcategoryurl]);

    useEffect(() => {
        if (!embla) {
            return;
        }
        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }
        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !bannerCarouselList || bannerCarouselList.length === 0) {
            return;
        }
        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, bannerCarouselList]);

    if (!product || !bannerCarouselList || bannerCarouselList.length === 0 || isProductLoading || isLoading) {
        return null;
    }

    return (
        <StyledWrapper {...styles.slideshowWrapper}>
            {fields.showArrows && bannerCarouselList.length > 1 && (
                <StyledWrapper {...styles.prevArrowWrapper}>
                    <Button {...styles.prevArrowButton} onClick={() => embla && embla.scrollPrev()}>
                        <ButtonIcon {...styles.iconProps} src={BannerArrow} />
                    </Button>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                <StyledWrapper {...styles.slideContainerWrapper}>
                    {(bannerCarouselList as BannerCarouselList[]).map((slide: BannerCarouselList, index: number) => (
                        <ADIPdpBannerSlide key={index} slide={slide} />
                    ))}
                </StyledWrapper>
            </StyledWrapper>
            {fields.slideIndicator && bannerCarouselList.length > 1 && (
                <StyledWrapper {...styles.dotsContainerWrapper}>
                    {(bannerCarouselList as BannerCarouselList[]).map((_: any, i: number) => {
                        const dotColorStyles =
                            i === selectedIndex
                                ? `
                                    background:  ${ADIColors.blue2};
                                    &:hover { background: ${ADIColors.white}; }
                                    &:active { background: ${ADIColors.blue2}; }
                                `
                                : "";
                        const dotButtonStyles = {
                            css: css`
                                ${styles.dotButton?.css || ""}
                                ${dotColorStyles}
                            `,
                        };
                        return <Button key={i} {...dotButtonStyles} onClick={() => embla && embla.scrollTo(i)} />;
                    })}
                </StyledWrapper>
            )}
            {fields.showArrows && bannerCarouselList.length > 1 && (
                <StyledWrapper {...styles.nextArrowWrapper}>
                    <Button {...styles.nextArrowButton} onClick={() => embla && embla.scrollNext()}>
                        <ButtonIcon {...styles.iconProps} src={BannerArrow} />
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(ADIPdpBanner)),
    definition: {
        group: "Product Details",
        icon: "Banner",
        displayName: "ADI PDP Banner",
        allowedContexts: [ProductDetailsPageContext],
        fieldDefinitions: [
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                min: 1,
            },
        ],
    },
};

export default widgetModule;

